import React, { useState, useEffect } from 'react';
import axios from 'axios';
import API_URL from '../../config';
import '../../App.css';

const InsuranceLeads = () => {
  const [leads, setLeads] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [statusFilter, setStatusFilter] = useState('ALL');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  useEffect(() => {
    fetchLeads();
  }, []);

  const fetchLeads = async () => {
    try {
      setIsLoading(true);
      setError('');
      const token = localStorage.getItem('token');
      
      if (!token) {
        setError('Please log in to view leads');
        return;
      }

      const response = await axios.get(
        `${API_URL}/api/insurance-leads`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      setLeads(response.data || []);
    } catch (err) {
      console.error('Error fetching leads:', err);
      setError('Failed to load insurance leads');
    } finally {
      setIsLoading(false);
    }
  };

  const handleExportCSV = async () => {
    try {
      const token = localStorage.getItem('token');
      const params = {};
      if (startDate) params.startDate = startDate;
      if (endDate) params.endDate = endDate;

      const response = await axios.get(
        `${API_URL}/api/insurance-leads/export`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
          params,
        }
      );

      // Download CSV
      const blob = new Blob([response.data.csv], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `insurance-leads-${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Error exporting CSV:', err);
      alert('Failed to export CSV');
    }
  };

  const handleMarkContacted = async (leadId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.patch(
        `${API_URL}/api/insurance-leads/${leadId}/status`,
        { status: 'CONTACTED' },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      fetchLeads();
    } catch (err) {
      console.error('Error updating lead:', err);
      alert('Failed to update lead status');
    }
  };

  const handleMarkConverted = async (leadId) => {
    const commissionAmount = prompt('Enter commission amount ($):');
    if (!commissionAmount) return;

    try {
      const token = localStorage.getItem('token');
      await axios.patch(
        `${API_URL}/api/insurance-leads/${leadId}/status`,
        {
          status: 'CONVERTED',
          commissionAmount: parseFloat(commissionAmount),
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      fetchLeads();
    } catch (err) {
      console.error('Error updating lead:', err);
      alert('Failed to update lead status');
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'NEW':
        return '#FF2D37';
      case 'CONTACTED':
        return '#F59E0B';
      case 'CONVERTED':
        return '#10B981';
      default:
        return '#6B7280';
    }
  };

  const filteredLeads = leads.filter(lead => {
    if (statusFilter !== 'ALL' && lead.status !== statusFilter) return false;
    if (startDate || endDate) {
      const leadDate = new Date(lead.createdAt);
      if (startDate && leadDate < new Date(startDate)) return false;
      if (endDate && leadDate > new Date(endDate)) return false;
    }
    return true;
  });

  const newLeadsCount = leads.filter(l => l.status === 'NEW').length;
  const totalValue = newLeadsCount * 150;

  if (isLoading) {
    return (
      <div className="admin-container">
        <div className="admin-loading">
          <div className="loading-spinner"></div>
          <p>Loading insurance leads...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-container">
      <header className="admin-header">
        <h1 className="admin-title">Insurance Leads</h1>
        <div className="admin-stats">
          <div className="admin-stat">
            <span className="stat-label">NEW Leads:</span>
            <span className="stat-value" style={{ color: '#FF2D37' }}>{newLeadsCount}</span>
          </div>
          <div className="admin-stat">
            <span className="stat-label">Total Value:</span>
            <span className="stat-value" style={{ color: '#10B981' }}>${totalValue.toLocaleString()}</span>
          </div>
        </div>
      </header>

      <div className="admin-controls">
        <div className="admin-filters">
          <select
            className="admin-filter-select"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="ALL">All Status</option>
            <option value="NEW">NEW</option>
            <option value="CONTACTED">CONTACTED</option>
            <option value="CONVERTED">CONVERTED</option>
          </select>
          <input
            type="date"
            className="admin-filter-date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            placeholder="Start Date"
          />
          <input
            type="date"
            className="admin-filter-date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            placeholder="End Date"
          />
        </div>
        <button
          className="admin-export-button"
          onClick={handleExportCSV}
        >
          Export CSV
        </button>
      </div>

      {error && (
        <div className="admin-error">
          <p>{error}</p>
        </div>
      )}

      <div className="admin-table-container">
        <table className="admin-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Email</th>
              <th>Phone</th>
              <th>DOT Number</th>
              <th>Fleet Size</th>
              <th>Cargo Type</th>
              <th>Status</th>
              <th>Created</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredLeads.length === 0 ? (
              <tr>
                <td colSpan="9" className="admin-table-empty">
                  No leads found
                </td>
              </tr>
            ) : (
              filteredLeads.map((lead) => (
                <tr key={lead.id}>
                  <td>{lead.firstName} {lead.lastName}</td>
                  <td>{lead.email}</td>
                  <td>{lead.phone}</td>
                  <td>{lead.dotNumber || 'N/A'}</td>
                  <td>{lead.numberOfTrucks || 'N/A'}</td>
                  <td>{lead.cargoType || 'N/A'}</td>
                  <td>
                    <span
                      className="admin-status-badge"
                      style={{ backgroundColor: getStatusColor(lead.status) }}
                    >
                      {lead.status}
                    </span>
                  </td>
                  <td>{formatDate(lead.createdAt)}</td>
                  <td>
                    <div className="admin-actions">
                      {lead.status === 'NEW' && (
                        <button
                          className="admin-action-button"
                          onClick={() => handleMarkContacted(lead.id)}
                        >
                          Mark Contacted
                        </button>
                      )}
                      {lead.status !== 'CONVERTED' && (
                        <button
                          className="admin-action-button converted"
                          onClick={() => handleMarkConverted(lead.id)}
                        >
                          Mark Converted
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default InsuranceLeads;



